/**
 * Navigation Bar Component
 *
 * A minimal header inspired by PICK PLAY design
 * Features:
 * - Clean, minimalist design with sticky positioning
 * - User balance (credits) display with coin icon
 * - Responsive design: popover menu on mobile, inline links on desktop
 * - Simple authentication buttons (LOGIN/SIGNUP)
 */
import { Plus } from "lucide-react";
import { Link } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * NavigationBar Component
 *
 * Minimal navigation header with user authentication state
 *
 * @param name - The authenticated user's name (if available)
 * @param avatarUrl - The user's avatar URL
 * @param credits - The user's current credit balance
 * @param loading - Boolean indicating if the auth state is still loading
 * @returns The navigation bar component
 */
export function NavigationBar({
  name,
  avatarUrl,
  credits = 0,
  loading,
  onRechargeClick,
}: {
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  credits?: number;
  loading: boolean;
  onRechargeClick?: () => void;
}) {
  const getInitial = (name?: string) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  return (
    <header className="sticky top-0 z-50 flex w-full items-center justify-between bg-[#E8E8E4] px-6 py-4 md:px-12 md:py-6">
      <div className="group cursor-pointer">
        <Link to="/">
          <h1 className="text-xl leading-none font-black tracking-tighter uppercase transition-opacity group-hover:opacity-70">
            PICK PLAY !
          </h1>
        </Link>
      </div>

      <div className="flex items-center gap-3 md:gap-8">
        {loading ? (
          // Loading state
          <div className="flex items-center">
            <div className="h-4 w-24 animate-pulse rounded-full bg-gray-400/20" />
          </div>
        ) : name ? (
          // Authenticated state
          <>
            {/* Credits display - always visible */}
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-black">
                {credits} <span className="md:hidden">C</span>
                <span className="hidden md:inline">Credits</span>
              </span>
              {onRechargeClick && (
                <button
                  onClick={onRechargeClick}
                  className="flex size-4 items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-gray-800"
                  aria-label="크레딧 충전"
                >
                  <Plus className="size-2" strokeWidth={3} />
                </button>
              )}
            </div>

            {/* Desktop/Tablet: Show name and links */}
            <div className="hidden items-center gap-6 md:flex">
              <span className="max-w-[120px] truncate text-sm font-bold tracking-wide text-black uppercase">
                {name}
              </span>

              <Link
                to="/my"
                className="border-b border-black pb-0.5 text-xs font-bold tracking-widest text-black uppercase transition-colors hover:border-gray-500 hover:text-gray-500"
              >
                My
              </Link>

              <Link
                to="/logout"
                className="border-b border-black pb-0.5 text-xs font-bold tracking-widest text-black uppercase transition-colors hover:border-gray-500 hover:text-gray-500"
              >
                Logout
              </Link>
            </div>

            {/* Mobile: Avatar with popover menu */}
            <div className="md:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center justify-center focus:outline-none"
                    aria-label="사용자 메뉴"
                  >
                    <Avatar className="size-8 cursor-pointer ring-2 ring-black/10 transition-all hover:ring-black/30">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
                      <AvatarFallback className="bg-black text-xs font-bold leading-none text-white">
                        {getInitial(name)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-40 rounded-xl border-black/10 bg-white p-0 shadow-lg"
                >
                  <div className="px-4 py-3">
                    <p className="truncate text-sm font-bold text-black">
                      {name}
                    </p>
                  </div>
                  <div className="h-px bg-black/10" />
                  <div className="p-1">
                    <Link
                      to="/my"
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                    >
                      My
                    </Link>
                    <Link
                      to="/logout"
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                    >
                      Logout
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </>
        ) : (
          // Unauthenticated state
          <div className="flex items-center gap-6">
            <Link
              to="/auth/login"
              className="text-xs font-bold tracking-tight text-black uppercase transition-colors hover:text-gray-500"
            >
              LOGIN
            </Link>

            <Link
              to="/auth/signup"
              className="text-xs font-bold tracking-tight text-black uppercase transition-colors hover:text-gray-500"
            >
              SIGNUP
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
